import { FieldStore, FormStore, ListFieldStore, required } from '@tom-ui/utils'
import { BerthResponse, Bollard } from '@yard/app/api'
import { action, makeObservable } from 'mobx'
import { IBerthFormData } from '../../models/yard-setup.model'

export type BollardSubFormType = {
  name: FieldStore<string>
  meterMark: FieldStore<number>
}

export class BerthFormStore extends FormStore<IBerthFormData> {
  name: FieldStore<string>
  quayMeterMarkFrom: FieldStore<number>
  quayMeterMarkTo: FieldStore<number>
  maxDraft: FieldStore<number>
  pierId: FieldStore<string>
  bollards: ListFieldStore<BollardSubFormType>

  constructor(data?: BerthResponse) {
    super()
    makeObservable(this, {
      addBollard: action,
      deleteBollard: action,
    })

    this.name = new FieldStore(data?.name ?? '', value => {
      if (!value) return 'fieldIsRequired'
    })

    this.quayMeterMarkFrom = new FieldStore(data?.quayMeterMarkFrom ?? 0, value => {
      if (!value) return 'valueMustBeGreaterThanZero'
      if (Number(value) <= 0) return 'valueMustBeGreaterThanZero'
    })

    this.quayMeterMarkTo = new FieldStore(data?.quayMeterMarkTo ?? 0, value => {
      if (!value) return 'valueMustBeGreaterThanZero'
      if (Number(value) <= Number(this.quayMeterMarkFrom.data ?? 0) + 1)
        return 'valueMustBeGreaterThanQuayMeterMarkFrom'
      if (Number(value) >= 999) return 'valueMustBeLessThanAThousand'
    })

    this.maxDraft = new FieldStore(data?.maxDraft ?? 0, value => {
      if (!value) return 'valueMustBeGreaterThanZero'
      if (Number(value) <= 0) return 'valueMustBeGreaterThanZero'
    })

    this.pierId = new FieldStore(data?.pierId ?? '', value => {
      if (!value) return 'fieldIsRequired'
    })

    this.bollards = new ListFieldStore<BollardSubFormType>(
      data?.bollards.map(bollard => this.createBollardSubForm(bollard)) ?? [],
    )
  }

  validate = () => {
    const isNameValid = this.name.validate()
    const isQuayMeterMarkFromValid = this.quayMeterMarkFrom.validate()
    const isQuayMeterMarkToValid = this.quayMeterMarkTo.validate()
    const isMaxDraftValid = this.maxDraft.validate()
    const isPierIdValid = this.pierId.validate()
    const areBollardsValid = this.bollards.value.every(bollard => {
      const isNameValid = bollard.name.validate()
      const isMeterMarkValid = bollard.meterMark.validate()
      return isNameValid && isMeterMarkValid
    })

    return (
      isNameValid &&
      isQuayMeterMarkFromValid &&
      isQuayMeterMarkToValid &&
      isMaxDraftValid &&
      isPierIdValid &&
      areBollardsValid
    )
  }

  addBollard = (value?: Bollard) => {
    this.bollards.push(this.createBollardSubForm(value))
  }

  deleteBollard = (index: number) => {
    this.bollards.removeByIndex(index)
  }

  get data(): IBerthFormData {
    return {
      ...super.data,
      name: this.name.data,
      quayMeterMarkFrom: this.quayMeterMarkFrom.data,
      quayMeterMarkTo: this.quayMeterMarkTo.data,
      maxDraft: this.maxDraft.data,
      pierId: this.pierId.data,
      bollards: this.bollards.data as Bollard[],
    }
  }

  private createBollardSubForm = (value?: Bollard): BollardSubFormType => ({
    name: new FieldStore(value?.name ?? '', required()),
    meterMark: new FieldStore(value?.meterMark ?? 0, meterMark => {
      if (!meterMark) return 'valueMustBeGreaterThanZero'

      if (meterMark < this.data.quayMeterMarkFrom) {
        return 'valueMustBeGreaterThanQuayMeterMarkFrom'
      }

      if (meterMark > this.data.quayMeterMarkTo) {
        return 'valueMustBeLessThanQuayMeterMarkTo'
      }
    }),
  })
}
